/**
 * Engine-hosted sales page for the SODA Intensive — a standalone 1:1 paid offer.
 *
 * Rendered by ProgramSalesPage when slug === 'soda-intensive' (mirrors how
 * MEMBERSHIP_CONTENT[slug] → <MembershipSalesPage>). The parent has already
 * fetched /api/public/programs/soda-intensive and applied the TSB v2 palette +
 * fonts to <html> as --prog-* / --font-* custom properties, so this component
 * just consumes them (with fallbacks).
 *
 * Migrated from the legacy HubSpot LP thesuccessfulbookkeeper.com/lp/soda-intensive
 * (2026-06-15). Copy carried over and tightened; testimonials kept anonymized
 * (no attribution on the source page). Voice: warm TSB operator.
 *
 * Structure (single-offer 1:1, not a cohort): hero → the big problem →
 * what is SODA (S/O/D/A) → the 1:1 offer (75-min two-part, Ron) → 2 testimonials
 * → is this for you → closing CTA. CTA opens a lightweight buyer-intent capture,
 * then navigates to /programs/soda-intensive/order where the Spiffy embed lives —
 * the same checkout path every other program uses.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProgramPublicResponse } from './ProgramSalesPage';
import { PolicyLinks } from './components/PolicyLinks';

const PRICE_LABEL = '$495 USD';

// Ron's canonical headshot on the R2 public bucket (people directory; ASSETS_PUBLIC_URL).
const RON_PHOTO_URL = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/ron-bester.png';

// SODA framework — the four pillars. Copy from the live LP, tightened.
const SODA_PILLARS = [
  { letter: 'S', word: 'Systemize', body: 'Document what works so others can repeat it — without you in the room.' },
  { letter: 'O', word: 'Outsource', body: "Let specialists do what's outside your zone of genius." },
  { letter: 'D', word: 'Delegate', body: 'Train and trust your team to take the reins.' },
  { letter: 'A', word: 'Automate', body: 'Use tech to eliminate the repetitive and the boring.' },
];

// Source LP carried no attribution; we use a truthful descriptor rather than invent names.
const TESTIMONIALS = [
  { quote: "I tried to do this myself and didn't realize how much I didn't know.", who: 'Bookkeeping business owner' },
  { quote: "Our revenue has quadrupled. I honestly don't know that we could've done it without it.", who: 'Bookkeeping business owner' },
];

const FIT_ITEMS = [
  "You're overwhelmed by the day-to-day operations",
  "You're the bottleneck in your own business",
  "You're ready to hire but don't know where to start",
  'You want to grow — but not at the expense of your life',
];

const HOW_IT_WORKS = [
  { n: '1', title: 'Book & pay', body: `One payment of ${PRICE_LABEL}. No subscription.` },
  { n: '2', title: 'Pick your time', body: "Choose a slot on Ron's calendar right after checkout — most owners meet within the week." },
  { n: '3', title: 'Meet & get your plan', body: '75 focused minutes with Ron, and a written action plan you can start the same week.' },
];

const FAQS = [
  {
    q: 'How is this different from a free discovery call?',
    a: 'A discovery call explores whether a program is right for you. The Intensive is a paid, focused working session — Ron audits your actual business and hands you a prioritized plan. You leave with deliverables, not just a conversation.',
  },
  {
    q: 'How soon can I book after paying?',
    a: "Right after checkout you pick your time on Ron's calendar. Most owners meet within the week.",
  },
  {
    q: "What if I'm just getting started — or already established?",
    a: 'SODA scales to where you are. Early on, it helps you build the right systems before bad habits set in. Further along, it pinpoints the one or two bottlenecks holding you back from growing.',
  },
  {
    q: 'Do I get anything to keep?',
    a: 'Yes — a written, prioritized SODA action plan from the session: what to systemize, outsource, delegate, and automate first, and how to start.',
  },
  {
    q: `Is the ${PRICE_LABEL} a subscription?`,
    a: 'No. One payment, no recurring billing. You book a single 75-minute Intensive.',
  },
  {
    q: "What if it's not worth it?",
    a: "You'll walk away with at least three specific, prioritized moves to buy back your time. If you don't — or you simply don't feel the session was worth it — tell us within 7 days and we'll refund every cent.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Buyer-intent capture → order page (same path the Clients program uses)
// ────────────────────────────────────────────────────────────────────────────

function BuyerIntentModal(props: {
  open: boolean;
  onClose: () => void;
  onSubmit: (d: { email: string; firstName: string; lastName: string }) => void;
}) {
  const { open, onClose, onSubmit } = props;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !firstName) {
      setError('First name and email are required.');
      return;
    }
    setSubmitting(true);
    onSubmit({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <button style={modalCloseBtn} onClick={onClose} aria-label="Close">×</button>
        <p style={modalEyebrow}>1:1 SODA Intensive · {PRICE_LABEL}</p>
        <h2 style={modalH2}>Where should we send your checkout?</h2>
        <p style={modalSub}>
          Your name and email so the checkout pre-fills, and so Ron knows you're on your way.
          You'll book your 75-minute session right after payment.
        </p>
        <form onSubmit={handle} style={modalForm}>
          <div style={modalRow}>
            <label style={modalLabel}>
              First name
              <input type="text" required autoFocus value={firstName}
                onChange={(e) => setFirstName(e.target.value)} style={modalInput} />
            </label>
            <label style={modalLabel}>
              Last name
              <input type="text" value={lastName}
                onChange={(e) => setLastName(e.target.value)} style={modalInput} />
            </label>
          </div>
          <label style={modalLabel}>
            Email
            <input type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} style={modalInput} />
          </label>
          {error && <p style={modalError}>{error}</p>}
          <button type="submit" disabled={submitting} style={modalSubmit}>
            {submitting ? 'One sec…' : 'Continue to checkout →'}
          </button>
          <p style={modalFineprint}>
            Continuing takes you to the secure checkout. Your card is charged through Spiffy —
            not stored here.
          </p>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function SodaIntensivePage({
  slug,
  data,
}: {
  slug: string;
  data: ProgramPublicResponse;
}) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const PASSTHROUGH_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'code',
  ];

  const handleIntent = (payload: { email: string; firstName: string; lastName: string }) => {
    const inbound = new URLSearchParams(window.location.search);
    const out = new URLSearchParams({
      em: payload.email,
      fn: payload.firstName,
      ln: payload.lastName,
    });
    for (const k of PASSTHROUGH_KEYS) {
      const v = inbound.get(k);
      if (v) out.set(k, v);
    }
    navigate(`/programs/${slug}/order?${out.toString()}`);
  };

  const openBuy = () => setModalOpen(true);

  return (
    <div style={pageWrap}>
      <style>{`
        .soda-pillar-letter { font-family: ${FONT_DISPLAY}; }
        .soda-fit-item::before { content: '✓'; margin-right: 10px; color: ${PROG_PRIMARY}; font-weight: 700; }
      `}</style>

      {/* ── Top nav ── */}
      <header style={topNav}>
        <div style={topNavInner}>
          {data.brand?.logoUrl && (
            <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
              <img src={data.brand.logoUrl} alt={data.brand.name} style={topNavLogo} />
            </a>
          )}
          <div style={topNavRight}>
            <span style={topNavMeta}>1:1 with Ron Bester · 75 minutes · {PRICE_LABEL}</span>
            <button onClick={openBuy} style={navCta}>Book your intensive</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section style={heroSection}>
        <div style={heroInner}>
          <p style={heroEyebrow}>The SODA Framework · Built for bookkeepers</p>
          <h1 style={heroH1}>Stop being the bottleneck in your own business.</h1>
          <p style={heroTagline}>
            Systemize, Outsource, Delegate &amp; Automate — buy back your time and grow.
          </p>
          <p style={heroSub}>
            The SODA Framework was built for bookkeepers ready to take back control, scale their
            business, and reclaim their life — without working more to get there.
          </p>
          <div style={heroCtaRow}>
            <button onClick={openBuy} style={primaryCta}>
              Book your 1:1 SODA Intensive — {PRICE_LABEL}
            </button>
          </div>
          <p style={heroFineprint}>
            75-minute, two-part audit with Ron Bester, VP Client Experience. One payment, no subscription.
          </p>
        </div>
      </section>

      {/* ── The big problem ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <p style={sectionLabel}>The Bookkeeping Business Owner's Big Problem</p>
          <h2 style={h2}>You didn't start your business to work more.</h2>
          <p style={lede}>
            But here you are — wearing every hat, chasing every task, and running out of steam.
            The truth is simple: the more your business grows, the more it demands systems. The
            SODA Framework gives you the roadmap to buy back your time, so you can lead, not just do.
          </p>
          <p style={researchLine}>
            Drawn from what The Successful Bookkeeper has learned working with 43,000+ bookkeepers.
          </p>
        </div>
      </section>

      {/* ── What is SODA ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>What Is SODA?</p>
            <h2 style={h2}>Four moves that take you out of the bottleneck</h2>
            <p style={{ ...lede, maxWidth: '620px', margin: '0 auto 44px' }}>
              It's not theory. It's an implementation framework tailored for busy bookkeepers who
              want to scale sustainably.
            </p>
          </div>
          <div style={pillarGrid}>
            {SODA_PILLARS.map((p) => (
              <div key={p.letter} style={pillarCard}>
                <span style={pillarLetter} className="soda-pillar-letter">{p.letter}</span>
                <p style={pillarWord}>{p.word}</p>
                <p style={pillarBody}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The 1:1 offer ── */}
      <section style={sectionNavy} id="offer">
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabelLight}>The 1:1 SODA Intensive</p>
            <h2 style={h2Inverse}>75 minutes that show you exactly where your time is leaking</h2>
            <p style={ledeInverse}>
              A focused, two-part audit of your business — and a plan you can put to work the
              same week.
            </p>
          </div>
          <div style={offerGrid}>
            <div style={offerCard}>
              <span style={offerTag}>Part 1 · 45 minutes</span>
              <h3 style={offerCardH3}>Workflow Analysis</h3>
              <p style={offerCardP}>
                Ron walks through your business with you to identify exactly where your time,
                energy, and profit are leaking — the tasks only you are doing that someone, or
                something, else should.
              </p>
            </div>
            <div style={offerCard}>
              <span style={offerTag}>Part 2 · 30 minutes</span>
              <h3 style={offerCardH3}>Your Action Plan</h3>
              <p style={offerCardP}>
                A tailored, prioritized plan to implement SODA in your business — what to
                systemize, outsource, delegate, and automate first, and how to start.
              </p>
            </div>
          </div>
          <div style={takeawayCard}>
            <span style={takeawayTag}>What you walk away with</span>
            <p style={takeawayText}>
              A written, prioritized SODA action plan — the exact moves to make first, in order,
              and how to start them this week. Yours to keep.
            </p>
          </div>

          <div style={facilitatorCard}>
            <img
              src={RON_PHOTO_URL}
              alt="Ron Bester"
              style={facilitatorPhoto}
              loading="lazy"
            />
            <div style={facilitatorBody}>
              <p style={facilitatorEyebrow}>Your guide</p>
              <p style={facilitatorName}>Ron Bester</p>
              <p style={facilitatorRole}>VP of Client Experience, The Successful Bookkeeper</p>
              <p style={facilitatorBio}>
                Ron helps bookkeepers find the system, support, and program that fit where they're
                headed. He runs every SODA Intensive himself — so the plan you leave with is built
                on your business, not a template.
              </p>
            </div>
          </div>

          <div style={facilitatorRow}>
            <button onClick={openBuy} style={primaryCta}>
              Book your 1:1 SODA Intensive — {PRICE_LABEL}
            </button>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={sectionTint}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>What Owners Say</p>
            <h2 style={h2}>You don't need to reinvent the wheel</h2>
            <p style={{ ...lede, maxWidth: '600px', margin: '0 auto 40px' }}>
              You need someone who's been there and can guide you with clarity and structure.
            </p>
          </div>
          <div style={quoteGrid}>
            {TESTIMONIALS.map((t, i) => (
              <blockquote key={i} style={quoteCard}>
                <p style={quoteText}>“{t.quote}”</p>
                <p style={quoteAttribution}>{t.who}</p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ── Is this for you ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Is This For You?</p>
            <h2 style={h2}>If any of this sounds like you, the Intensive is built for you</h2>
          </div>
          <ul style={fitList}>
            {FIT_ITEMS.map((item, i) => (
              <li key={i} style={fitItem} className="soda-fit-item">{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>How It Works</p>
            <h2 style={h2}>From checkout to your plan, in three steps</h2>
          </div>
          <div style={stepGrid}>
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} style={stepCard}>
                <span style={stepNum}>{s.n}</span>
                <p style={stepTitle}>{s.title}</p>
                <p style={stepBody}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={section}>
        <div style={{ ...sectionInner, maxWidth: '720px' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Questions</p>
            <h2 style={h2}>Before you book</h2>
          </div>
          <div style={faqList}>
            {FAQS.map((f, i) => (
              <div key={i} style={faqItem}>
                <p style={faqQ}>{f.q}</p>
                <p style={faqA}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section style={finalSection}>
        <div style={sectionInner}>
          <p style={finalPre}>Get the clarity and structure you need to lead with confidence.</p>
          <h2 style={h2Inverse}>Book your 75-minute SODA Intensive and start systemizing your way to freedom.</h2>
          <div style={guaranteeCard}>
            <span style={guaranteeBadge}>Our guarantee</span>
            <p style={guaranteeText}>
              You'll walk away with at least three specific, prioritized moves to buy back your time.
              If you don't — or you simply don't feel it was worth it — tell us within 7 days and
              we'll refund every cent.
            </p>
          </div>
          <div style={heroCtaRow}>
            <button onClick={openBuy} style={primaryCta}>
              Book your 1:1 SODA Intensive — {PRICE_LABEL}
            </button>
          </div>
          <p style={finalReassure}>
            No upsell pressure — 75 minutes focused entirely on your business, and a plan you can act on.
          </p>
          <p style={{ ...heroFineprint, marginTop: '14px' }}>
            One payment of {PRICE_LABEL}. Book your session immediately after checkout.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={footer}>
        <p style={footerMeta}>
          The SODA Intensive · 75-minute 1:1 audit with Ron Bester · {PRICE_LABEL} · The Successful Bookkeeper
        </p>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>

      <BuyerIntentModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleIntent} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tokens (consumed from <html> custom props set by ProgramSalesPage; fallbacks
// are the TSB v2 SODA palette so the page is sane even before tokens apply)
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #1A1AFF)';
const PROG_DEEP = 'var(--prog-deep, #1A1A3E)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const PROG_TINT = 'var(--prog-tint, #E8E8FF)';
const PROG_HIGHLIGHT = 'var(--prog-highlight, #FF6B35)';
const PROG_CANVAS = 'var(--prog-canvas, #F8F6F3)';
// Headings use Poppins, not the serif display face (Michael, 2026-06-16).
// Poppins needs heavier weight + tighter tracking than Fraunces to read as display.
const FONT_DISPLAY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  background: '#FFFFFF',
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
  fontSize: '17px',
  lineHeight: 1.6,
};

// Top nav
const topNav: React.CSSProperties = {
  background: '#FFFFFF',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  padding: '12px 24px',
  position: 'sticky',
  top: 0,
  zIndex: 50,
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1080px', margin: '0 auto', display: 'flex',
  alignItems: 'center', justifyContent: 'space-between', gap: '16px',
};
const topNavLogoLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const topNavLogo: React.CSSProperties = { height: '34px', width: 'auto', display: 'block' };
const topNavRight: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '18px' };
const topNavMeta: React.CSSProperties = {
  fontSize: '12.5px', fontWeight: 600, color: PROG_TEXT_DARK, opacity: 0.7,
};
const navCta: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '13px',
  fontWeight: 700, padding: '9px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer',
};

// Sections
const section: React.CSSProperties = { padding: '76px 24px' };
const sectionPale: React.CSSProperties = { ...section, background: PROG_CANVAS };
const sectionTint: React.CSSProperties = { ...section, background: PROG_TINT };
const sectionNavy: React.CSSProperties = { ...section, background: PROG_DEEP };
const sectionInner: React.CSSProperties = { maxWidth: '960px', margin: '0 auto' };

const sectionLabel: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 12px',
};
const sectionLabelLight: React.CSSProperties = { ...sectionLabel, color: PROG_HIGHLIGHT };

const h2: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(26px, 3.4vw, 36px)', fontWeight: 700,
  lineHeight: 1.15, letterSpacing: '-0.015em', margin: '0 0 18px', color: PROG_TEXT_DARK,
};
const h2Inverse: React.CSSProperties = { ...h2, color: '#FFFFFF' };

const lede: React.CSSProperties = {
  fontSize: '18px', lineHeight: 1.6, color: PROG_TEXT_DARK, opacity: 0.85, margin: '0 0 8px',
};
const ledeInverse: React.CSSProperties = {
  ...lede, color: 'rgba(255,255,255,0.82)', opacity: 1, maxWidth: '620px', margin: '0 auto',
};
const researchLine: React.CSSProperties = {
  fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase',
  color: PROG_PRIMARY, opacity: 0.85, margin: '20px 0 0',
};

// Hero
const heroSection: React.CSSProperties = {
  padding: '92px 24px 84px',
  background: `linear-gradient(160deg, ${PROG_CANVAS} 0%, ${PROG_TINT} 100%)`,
  borderBottom: '1px solid rgba(10,10,46,0.06)',
};
const heroInner: React.CSSProperties = { maxWidth: '880px', margin: '0 auto', textAlign: 'center' };
const heroEyebrow: React.CSSProperties = {
  fontSize: '12px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 18px',
};
const heroH1: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(34px, 5.2vw, 56px)', fontWeight: 800,
  lineHeight: 1.05, letterSpacing: '-0.025em', margin: '0 0 18px', color: PROG_TEXT_DARK,
};
// The SODA expansion that used to be the H1; now a tagline beneath the headline.
const heroTagline: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(18px, 2.2vw, 23px)', fontWeight: 600,
  lineHeight: 1.3, letterSpacing: '-0.01em', color: PROG_PRIMARY,
  maxWidth: '680px', margin: '0 auto 20px',
};
const heroSub: React.CSSProperties = {
  fontSize: '19px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.82,
  maxWidth: '640px', margin: '0 auto 32px',
};
const heroCtaRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '14px',
};
const primaryCta: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '17px',
  fontWeight: 700, padding: '16px 30px', border: 'none', borderRadius: '10px',
  cursor: 'pointer', boxShadow: '0 8px 24px rgba(26,26,255,0.22)',
};
const heroFineprint: React.CSSProperties = {
  fontSize: '14px', color: PROG_TEXT_DARK, opacity: 0.6, margin: '22px 0 0',
};

// Pillars
const pillarGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px',
};
const pillarCard: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '18px',
  padding: '28px 24px', boxShadow: '0 2px 14px rgba(10,10,46,0.04)',
};
const pillarLetter: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '52px', height: '52px', borderRadius: '14px', background: PROG_TINT,
  color: PROG_PRIMARY, fontSize: '26px', fontWeight: 700, marginBottom: '16px',
};
const pillarWord: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.01em',
  margin: '0 0 8px', color: PROG_TEXT_DARK,
};
const pillarBody: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.78, margin: 0,
};

// Offer
const offerGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '20px', margin: '44px 0 36px',
};
const offerCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '18px', padding: '28px 26px',
};
const offerTag: React.CSSProperties = {
  display: 'inline-block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: PROG_HIGHLIGHT, marginBottom: '12px',
};
const offerCardH3: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '23px', fontWeight: 700, letterSpacing: '-0.01em',
  margin: '0 0 10px', color: '#fff',
};
const offerCardP: React.CSSProperties = {
  fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(255,255,255,0.78)', margin: 0,
};
// Tangible deliverable callout (full-width, highlight accent) inside the navy offer.
const takeawayCard: React.CSSProperties = {
  background: 'rgba(255,107,53,0.10)', border: `1px solid ${PROG_HIGHLIGHT}`,
  borderRadius: '16px', padding: '22px 26px', margin: '0 0 28px', textAlign: 'center',
};
const takeawayTag: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: PROG_HIGHLIGHT, marginBottom: '8px',
};
const takeawayText: React.CSSProperties = {
  fontSize: '16.5px', lineHeight: 1.5, color: 'rgba(255,255,255,0.92)', margin: 0,
};

// Facilitator card — Ron's photo + bio, on the navy offer background.
const facilitatorCard: React.CSSProperties = {
  display: 'flex', gap: '22px', alignItems: 'center', flexWrap: 'wrap',
  justifyContent: 'center', textAlign: 'left',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '18px', padding: '24px 26px', margin: '0 0 32px',
};
const facilitatorPhoto: React.CSSProperties = {
  width: '96px', height: '96px', borderRadius: '50%', objectFit: 'cover',
  border: '2px solid rgba(255,255,255,0.25)', flexShrink: 0,
};
const facilitatorBody: React.CSSProperties = { flex: 1, minWidth: '240px' };
const facilitatorEyebrow: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: PROG_HIGHLIGHT, margin: '0 0 6px',
};
const facilitatorName: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.01em',
  color: '#fff', margin: '0 0 2px',
};
const facilitatorRole: React.CSSProperties = {
  fontSize: '13.5px', fontWeight: 600, color: 'rgba(255,255,255,0.72)', margin: '0 0 10px',
};
const facilitatorBio: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: 'rgba(255,255,255,0.82)', margin: 0,
};

const facilitatorRow: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', textAlign: 'center',
};

// Testimonials
const quoteGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px',
};
const quoteCard: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '18px',
  padding: '30px 28px', margin: 0, borderLeft: `4px solid ${PROG_PRIMARY}`,
};
const quoteText: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '20px', lineHeight: 1.42, fontWeight: 500,
  letterSpacing: '-0.01em', color: PROG_TEXT_DARK, margin: '0 0 14px',
};
const quoteAttribution: React.CSSProperties = {
  fontFamily: FONT_BODY, fontSize: '13px', fontWeight: 600, letterSpacing: '0.02em',
  textTransform: 'uppercase', color: PROG_PRIMARY, margin: 0,
};

// Fit
const fitList: React.CSSProperties = {
  listStyle: 'none', padding: 0, margin: '36px auto 0', maxWidth: '560px',
  display: 'flex', flexDirection: 'column', gap: '14px',
};
const fitItem: React.CSSProperties = {
  fontSize: '17px', lineHeight: 1.45, color: PROG_TEXT_DARK, display: 'flex', alignItems: 'baseline',
};

// How it works (3 steps)
const stepGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '20px', margin: '44px 0 0',
};
const stepCard: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '18px',
  padding: '26px 24px', textAlign: 'center', boxShadow: '0 2px 14px rgba(10,10,46,0.04)',
};
const stepNum: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '40px', height: '40px', borderRadius: '50%', background: PROG_PRIMARY,
  color: '#fff', fontSize: '18px', fontWeight: 700, marginBottom: '14px',
};
const stepTitle: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em',
  color: PROG_TEXT_DARK, margin: '0 0 8px',
};
const stepBody: React.CSSProperties = {
  fontSize: '14.5px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.78, margin: 0,
};

// FAQ
const faqList: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '16px', margin: '36px 0 0',
};
const faqItem: React.CSSProperties = {
  background: PROG_CANVAS, border: '1px solid rgba(10,10,46,0.07)', borderRadius: '14px',
  padding: '20px 22px',
};
const faqQ: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.01em',
  color: PROG_TEXT_DARK, margin: '0 0 8px',
};
const faqA: React.CSSProperties = {
  fontSize: '15.5px', lineHeight: 1.6, color: PROG_TEXT_DARK, opacity: 0.82, margin: 0,
};

// Final
const finalSection: React.CSSProperties = {
  padding: '84px 24px', background: PROG_DEEP, textAlign: 'center',
};
const finalPre: React.CSSProperties = {
  fontSize: '15px', fontWeight: 600, letterSpacing: '0.04em', color: PROG_HIGHLIGHT, margin: '0 0 16px',
};
const finalReassure: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: 'rgba(255,255,255,0.78)',
  maxWidth: '520px', margin: '22px auto 0',
};
// Risk reversal — outcome + satisfaction guarantee (Michael, 2026-06-16).
const guaranteeCard: React.CSSProperties = {
  maxWidth: '600px', margin: '0 auto 30px', padding: '22px 28px',
  background: 'rgba(255,107,53,0.08)', border: `1px solid ${PROG_HIGHLIGHT}`,
  borderRadius: '16px', textAlign: 'center',
};
const guaranteeBadge: React.CSSProperties = {
  display: 'inline-block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: PROG_HIGHLIGHT, marginBottom: '8px',
};
const guaranteeText: React.CSSProperties = {
  fontSize: '15.5px', lineHeight: 1.55, color: 'rgba(255,255,255,0.92)', margin: 0,
};

// Footer
const footer: React.CSSProperties = {
  padding: '36px 24px', textAlign: 'center', background: '#fff',
  borderTop: '1px solid rgba(10,10,46,0.08)',
};
const footerMeta: React.CSSProperties = {
  fontSize: '13px', color: PROG_TEXT_DARK, opacity: 0.6, margin: 0,
};

// Modal
const modalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(10,10,46,0.55)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 100,
};
const modalCard: React.CSSProperties = {
  background: '#fff', borderRadius: '18px', padding: '34px 32px', maxWidth: '440px',
  width: '100%', position: 'relative', boxShadow: '0 24px 60px rgba(10,10,46,0.3)',
};
const modalCloseBtn: React.CSSProperties = {
  position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none',
  fontSize: '26px', lineHeight: 1, color: PROG_TEXT_DARK, opacity: 0.4, cursor: 'pointer',
};
const modalEyebrow: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 10px',
};
const modalH2: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 700, letterSpacing: '-0.015em',
  margin: '0 0 10px', color: PROG_TEXT_DARK,
};
const modalSub: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.75, margin: '0 0 22px',
};
const modalForm: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '14px' };
const modalRow: React.CSSProperties = { display: 'flex', gap: '12px', flexWrap: 'wrap' };
const modalLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px',
  fontWeight: 600, color: PROG_TEXT_DARK, flex: 1, minWidth: '140px',
};
const modalInput: React.CSSProperties = {
  padding: '11px 13px', fontSize: '15px', border: '1px solid #D1D1D6', borderRadius: '8px',
  fontFamily: FONT_BODY, color: PROG_TEXT_DARK, fontWeight: 400,
};
const modalError: React.CSSProperties = { color: '#B91C1C', fontSize: '13px', margin: 0 };
const modalSubmit: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '16px',
  fontWeight: 700, padding: '14px', border: 'none', borderRadius: '10px', cursor: 'pointer', marginTop: '4px',
};
const modalFineprint: React.CSSProperties = {
  fontSize: '12px', lineHeight: 1.5, color: PROG_TEXT_DARK, opacity: 0.55, margin: 0, textAlign: 'center',
};
